import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/TextArea";
import { Label } from "@/components/ui/Label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/RadioGroup";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { Checkbox } from "@/components/ui/CheckBox";
import { Loader2 } from "lucide-react";
import useDocumentTitle from "@/hooks/UseDocumentTitle";
import {
  useGetFormBySlugQuery,
  useGetFormSubmissionQuery,
} from "@/services/FormBuilderApi";
import { useParams, useSearchParams } from "react-router-dom";

// ============================================================================
// TYPES
// ============================================================================

interface Field {
  id: string;
  type: string;
  label: string;
  placeholder?: string;
  helpText?: string;
  required: boolean;
  options?: string[];
  conditions?: Condition[];
  min?: number;
  max?: number;
  rows?: number;
  accept?: string;
  order?: number;
  apiIntegration?: ApiIntegration;
  sectionId?: string;
  sectionTitle?: string;
  layout?: {
    columns: number;
    order: number;
  };
}

interface Condition {
  id: string;
  sourceField: string;
  operator: string;
  value: string;
  action: string;
}

interface ApiIntegration {
  enabled: boolean;
  endpoint: string;
  method: string;
  token?: string;
  responseMappings: ResponseMapping[];
}

interface ResponseMapping {
  id: string;
  responsePath: string;
  targetField: string;
}

interface GeneratedForm {
  id?: string;
  title: string;
  description: string;
  slug?: string;
  status?: string;
  fields: Field[];
}

// ============================================================================
// CONDITIONAL LOGIC EVALUATION
// ============================================================================

const evaluateFieldConditions = (
  field: Field,
  formData: Record<string, any>
): { shouldShow: boolean; isRequired: boolean } => {
  if (!field.conditions || field.conditions.length === 0) {
    return {
      shouldShow: true,
      isRequired: field.required || false,
    };
  }

  let shouldShow = true;
  let isRequired = field.required || false;

  field.conditions.forEach((condition) => {
    const sourceValue = formData[condition.sourceField];
    let conditionMet = false;

    switch (condition.operator) {
      case "equals":
        conditionMet = Array.isArray(sourceValue)
          ? sourceValue.length === 1 && sourceValue[0] === condition.value
          : sourceValue === condition.value;
        break;
      case "not_equals":
        conditionMet = Array.isArray(sourceValue)
          ? !(sourceValue.length === 1 && sourceValue[0] === condition.value)
          : sourceValue !== condition.value;
        break;
      case "includes":
        conditionMet = Array.isArray(sourceValue)
          ? sourceValue.includes(condition.value)
          : sourceValue?.toString().includes(condition.value);
        break;
      case "not_includes":
        conditionMet = Array.isArray(sourceValue)
          ? !sourceValue.includes(condition.value)
          : !sourceValue?.toString().includes(condition.value);
        break;
      case "is_empty":
        conditionMet = Array.isArray(sourceValue)
          ? sourceValue.length === 0
          : !sourceValue || sourceValue.toString().trim() === "";
        break;
      case "is_not_empty":
        conditionMet = Array.isArray(sourceValue)
          ? sourceValue.length > 0
          : sourceValue && sourceValue.toString().trim() !== "";
        break;
    }

    if (conditionMet) {
      switch (condition.action) {
        case "show":
          shouldShow = true;
          break;
        case "hide":
          shouldShow = false;
          break;
        case "require":
          isRequired = true;
          break;
        case "optional":
          isRequired = false;
          break;
      }
    } else {
      if (condition.action === "show") {
        shouldShow = false;
      } else if (condition.action === "hide") {
        shouldShow = true;
      }
    }
  });

  return { shouldShow, isRequired };
};

// ============================================================================
// FIELD RENDERER COMPONENT
// ============================================================================

const FieldRenderer = ({
  field,
  formData,
  onInputChange,
  onApiTrigger,
}: {
  field: Field;
  formData: Record<string, any>;
  onInputChange: (fieldId: string, value: any, isFile?: boolean) => void;
  onApiTrigger: (fieldId: string, value: any) => void;
}) => {
  switch (field.type) {
    case "radio":
      return (
        <RadioGroup
          value={formData[field.id] || ""}
          onValueChange={(value) => {
            onInputChange(field.id, value);
            onApiTrigger(field.id, value);
          }}
          disabled
        >
          {field.options?.map((option, idx) => (
            <div key={idx} className="flex items-center space-x-2">
              <RadioGroupItem value={option} id={`${field.id}-${idx}`} disabled />
              <Label
                htmlFor={`${field.id}-${idx}`}
                className="font-normal cursor-not-allowed"
              >
                {option}
              </Label>
            </div>
          ))}
        </RadioGroup>
      );

    case "select":
      return (
        <>
          <Select
            value={formData[field.id] || ""}
            onValueChange={(value) => {
              onInputChange(field.id, value);
              onApiTrigger(field.id, value);
            }}
            disabled
          >
            <SelectTrigger disabled>
              <SelectValue
                placeholder={field.placeholder || "Select an option"}
              />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option, idx) => (
                <SelectItem key={idx} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {field.helpText && (
            <p className="text-sm text-gray-500">{field.helpText}</p>
          )}
        </>
      );

    case "checkbox":
      return (
        <>
          <div className="space-y-2">
            {field.options?.map((option, idx) => (
              <div key={idx} className="flex items-center space-x-2">
                <Checkbox
                  id={`${field.id}-${idx}`}
                  checked={(formData[field.id] || []).includes(option)}
                  onCheckedChange={(checked) => {
                    const current = formData[field.id] || [];
                    const newValue = checked
                      ? [...current, option]
                      : current.filter((v: any) => v !== option);
                    onInputChange(field.id, newValue);
                    onApiTrigger(field.id, newValue);
                  }}
                  disabled
                />
                <Label
                  htmlFor={`${field.id}-${idx}`}
                  className="font-normal cursor-not-allowed"
                >
                  {option}
                </Label>
              </div>
            ))}
          </div>
          {field.helpText && (
            <p className="text-sm text-gray-500">{field.helpText}</p>
          )}
        </>
      );

    case "textarea":
      return (
        <>
          <Textarea
            placeholder={field.placeholder}
            value={formData[field.id] || ""}
            onChange={(e) => onInputChange(field.id, e.target.value)}
            onBlur={() => onApiTrigger(field.id, formData[field.id])}
            rows={field.rows || 4}
            className="resize-none"
            readOnly
            disabled
          />
          {field.helpText && (
            <p className="text-sm text-gray-500">{field.helpText}</p>
          )}
        </>
      );

    case "file":
      return (
        <>
          {formData[field.id] ? (
            <div className="space-y-2">
              {typeof formData[field.id] === 'string' ? (
                // File URL from API
                <div className="flex items-center gap-2 p-3 border rounded-md bg-gray-50">
                  <a
                    href={formData[field.id]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    View File
                  </a>
                </div>
              ) : (
                // File object from file input
                <p className="text-sm text-gray-500 p-3 border rounded-md bg-gray-50">
                  Selected file: {formData[field.id].name}
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-400 p-3 border rounded-md bg-gray-50">No file uploaded</p>
          )}
          {field.helpText && (
            <p className="text-sm text-gray-500">{field.helpText}</p>
          )}
        </>
      );

    default:
      return (
        <>
          <Input
            type={field.type === "datetime" ? "datetime-local" : field.type}
            placeholder={field.placeholder}
            value={formData[field.id] || ""}
            onChange={(e) => onInputChange(field.id, e.target.value)}
            onBlur={() => onApiTrigger(field.id, formData[field.id])}
            min={field.min}
            max={field.max}
            required={field.required}
            readOnly
            disabled
          />
          {field.helpText && (
            <p className="text-sm text-gray-500">{field.helpText}</p>
          )}
        </>
      );
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const FormViewer = () => {
  useDocumentTitle("Form Viewer");
  const { slug, id } = useParams<{ slug: string; id: string }>();
  const [generatedForm, setGeneratedForm] = useState<GeneratedForm | null>(
    null
  );
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [sections, setSections] = useState<
    Record<string, { title: string; collapsed: boolean }>
  >({});

  const { data: formFromApi, isLoading } = useGetFormBySlugQuery(slug!, {
    skip: !slug,
  });

  const { data: filledData } = useGetFormSubmissionQuery(id!, {
    skip: !id,
  });

  useEffect(() => {
    if (filledData) {
      setFormData(filledData.data)
    }
  }, [filledData]);

  // Load form from API
  useEffect(() => {
    if (formFromApi && slug) {
      // Build sections state from API data
      const sectionsMap: Record<string, { title: string; collapsed: boolean }> =
        {};
      formFromApi.fields.forEach((field) => {
        if (field.section_id && !sectionsMap[field.section_id]) {
          sectionsMap[field.section_id] = {
            title: field.section_title || "Untitled Section",
            collapsed: false,
          };
        }
      });
      setSections(sectionsMap);

      setGeneratedForm({
        id: formFromApi.id,
        title: formFromApi.name,
        description: formFromApi.description || "",
        slug: formFromApi.slug,
        status: formFromApi.status,
        fields: [...formFromApi.fields]
          .sort((a: any, b: any) => a.order - b.order)
          .map((field: any) => ({
            id: field.field_id || field.id.toString(),
            type: field.type,
            label: field.label,
            placeholder: field.placeholder || "",
            helpText: field.helper_text || "",
            required: field.required || false,
            options: field.options || [],
            conditions: field.conditions || [],
            min: field.min ?? undefined,
            max: field.max ?? undefined,
            rows: field.rows ?? undefined,
            accept: field.accept || undefined,
            order: field.order,
            sectionId: field.section_id || undefined,
            sectionTitle: field.section_title || undefined,
            layout: field.layout || undefined,
            apiIntegration: field.api_integration || {
              enabled: false,
              endpoint: "",
              method: "GET",
              token: "",
              responseMappings: [],
            },
          })),
      } as any);
    }
  }, [formFromApi, slug]);

  const handleApiTrigger = async (fieldId: string, value: any) => {
    const field = generatedForm?.fields.find((f) => f.id === fieldId);
    if (!field?.apiIntegration?.enabled || !value) return;

    try {
      const endpoint = field.apiIntegration.endpoint.replace(
        "{value}",
        encodeURIComponent(value)
      );
      const response = await fetch(endpoint, {
        method: field.apiIntegration.method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${field.apiIntegration.token}`,
        },
      });

      if (!response.ok) throw new Error("API error");
      const data = await response.json();

      const newFormData = { ...formData };
      field.apiIntegration.responseMappings.forEach((mapping) => {
        if (!mapping.targetField) return;

        const rawPath = mapping.responsePath?.trim();
        if (!rawPath) return;

        let val: any;
        if (rawPath.startsWith(`"`) && rawPath.endsWith(`"`)) {
          val = rawPath.slice(1, -1);
        } else {
          const source = Array.isArray(data) ? data[0] : data;
          val = source?.[rawPath];
        }

        if (val !== undefined) {
          newFormData[mapping.targetField] = val;
        }
      });
    } catch (error) {
      console.error("API integration error:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 flex overflow-hidden">
      <div className="flex-1 overflow-auto bg-gray-50">
        <div className="max-w-4xl mx-auto p-6">
          {generatedForm ? (
            <Card>
              <CardHeader className="bg-primary text-white">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-2xl font-bold">{generatedForm.title}</p>
                    <p className="text-sm text-white/90 mt-1">
                      {generatedForm.description}
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6 min-h-[400px]">
                {generatedForm.fields.length === 0 ? (
                  <div className="flex items-center justify-center h-[400px] text-gray-500">
                    <p>No fields in this form</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {(() => {
                      // Group fields by section while preserving order
                      const sectionOrder: string[] = [];
                      const groupedBySection = generatedForm.fields.reduce(
                        (acc, field) => {
                          const key = field.sectionId || "ungrouped";
                          if (!acc[key]) {
                            acc[key] = {
                              title:
                                field.sectionTitle ||
                                sections[key]?.title ||
                                "Untitled Section",
                              fields: [],
                            };
                            if (!sectionOrder.includes(key)) {
                              sectionOrder.push(key);
                            }
                          }
                          acc[key].fields.push(field);
                          return acc;
                        },
                        {} as Record<string, { title: string; fields: Field[] }>
                      );

                      // Ensure ungrouped fields appear last if they exist
                      if (sectionOrder.includes("ungrouped")) {
                        sectionOrder.push(
                          sectionOrder.splice(
                            sectionOrder.indexOf("ungrouped"),
                            1
                          )[0]
                        );
                      }

                      return Object.entries(groupedBySection)
                        .sort(([keyA], [keyB]) => {
                          return (
                            sectionOrder.indexOf(keyA) -
                            sectionOrder.indexOf(keyB)
                          );
                        })
                        .map(([sectionId, group]) => {
                          const isCollapsed =
                            sections[sectionId]?.collapsed ?? false;

                          return (
                            <div key={sectionId} className="mb-12">
                              {/* Section Header */}
                              {sectionId !== "ungrouped" && (
                                <div
                                  className="flex items-center justify-between p-5 bg-primary/5 rounded-lg cursor-pointer hover:bg-primary/10 transition mb-6 border border-primary/20 select-none"
                                  onClick={() =>
                                    setSections((prev) => ({
                                      ...prev,
                                      [sectionId]: {
                                        ...prev[sectionId],
                                        collapsed: !prev[sectionId]?.collapsed,
                                      },
                                    }))
                                  }
                                >
                                  <h3 className="font-semibold text-lg text-primary">
                                    {group.title}
                                  </h3>
                                  <span className="text-sm text-gray-600">
                                    {group.fields.length} field
                                    {group.fields.length !== 1 ? "s" : ""}
                                    {isCollapsed ? " ▸" : " ▾"}
                                  </span>
                                </div>
                              )}

                              {!isCollapsed && (
                                <div
                                  className={
                                    sectionId !== "ungrouped" ? "pl-8" : ""
                                  }
                                >
                                  {/* Row Grouping Inside Section */}
                                  {(() => {
                                    const rows: Field[][] = [];
                                    let currentRow: Field[] = [];

                                    group.fields.forEach((field) => {
                                      const cols = field.layout?.columns || 1;

                                      if (cols === 1) {
                                        if (currentRow.length > 0) {
                                          rows.push(currentRow);
                                          currentRow = [];
                                        }
                                        rows.push([field]);
                                      } else {
                                        if (
                                          currentRow.length > 0 &&
                                          currentRow[0].layout?.columns !== cols
                                        ) {
                                          rows.push(currentRow);
                                          currentRow = [];
                                        }
                                        currentRow.push(field);
                                        if (currentRow.length === cols) {
                                          rows.push(currentRow);
                                          currentRow = [];
                                        }
                                      }
                                    });
                                    if (currentRow.length > 0)
                                      rows.push(currentRow);

                                    return rows.map((row, rowIndex) => {
                                      const columnCount =
                                        row[0]?.layout?.columns || 1;

                                      return (
                                        <div
                                          key={rowIndex}
                                          className={`grid gap-6 mb-6 ${columnCount === 1
                                            ? "grid-cols-1"
                                            : columnCount === 2
                                              ? "grid-cols-1 md:grid-cols-2"
                                              : columnCount === 3
                                                ? "grid-cols-1 md:grid-cols-3"
                                                : "grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
                                            }`}
                                        >
                                          {row.map((field) => {
                                            const { shouldShow, isRequired } =
                                              evaluateFieldConditions(
                                                field,
                                                formData
                                              );

                                            if (!shouldShow) return null;

                                            return (
                                              <div
                                                key={field.id}
                                                className="relative group"
                                              >
                                                <div className="space-y-3 p-6 border rounded-lg bg-white">
                                                  <div className="flex items-start justify-between gap-2">
                                                    <Label className="text-base font-medium">
                                                      {field.label}
                                                      {isRequired && (
                                                        <span className="text-red-500 ml-1">
                                                          *
                                                        </span>
                                                      )}
                                                    </Label>
                                                  </div>
                                                  <FieldRenderer
                                                    field={field}
                                                    formData={formData}
                                                    onInputChange={
                                                      () => { }
                                                    }
                                                    onApiTrigger={
                                                      handleApiTrigger
                                                    }
                                                  />
                                                </div>
                                              </div>
                                            );
                                          })}

                                          {/* Fill empty cells */}
                                          {columnCount > row.length &&
                                            Array(columnCount - row.length)
                                              .fill(null)
                                              .map((_, i) => (
                                                <div key={`empty-${i}`} />
                                              ))}
                                        </div>
                                      );
                                    });
                                  })()}
                                </div>
                              )}
                            </div>
                          );
                        });
                    })()}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="flex items-center justify-center h-screen">
              <p className="text-gray-500">Form Submission not found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormViewer;
